import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import classes from './styles.module.css';
import Api from '../../../Api';
import Loader from '../../Utils/Loader';

// Status pill
function StatusPill({ status }) {
    const intl = useIntl();
    const statusConfig = {
        new: { label: intl.formatMessage({ id: 'Disputes.status.new' }), bg: '#e3f2fd', color: '#1565c0' },
        in_progress: {
            label: intl.formatMessage({ id: 'Disputes.status.inProgress' }),
            bg: '#fff3e0',
            color: '#e65100',
        },
        resolved: { label: intl.formatMessage({ id: 'Disputes.status.resolved' }), bg: '#e8f5e9', color: '#2e7d32' },
        archived: { label: intl.formatMessage({ id: 'Disputes.status.archived' }), bg: '#f5f5f5', color: '#757575' },
    };

    const config = statusConfig[status] || { label: status, bg: '#f5f5f5', color: '#666' };

    return (
        <span className={classes.StatusPill} style={{ background: config.bg, color: config.color }}>
            {config.label}
        </span>
    );
}

function AttachmentsList({ attachments, disputeId, token }) {
    const intl = useIntl();
    const [loadingIndex, setLoadingIndex] = useState(null);

    async function handleOpenAttachment(att, idx) {
        setLoadingIndex(idx);
        try {
            const api = new Api({ token });
            // Каждое вложение может принадлежать другому disputeId (медиагруппа)
            const attDisputeId = att.disputeId || disputeId;
            // Находим индекс этого вложения внутри его dispute
            const attIndex = att.indexInDispute != null ? att.indexInDispute : 0;
            const result = await api.getDisputeAttachmentUrl(attDisputeId, attIndex);
            if (result.error) {
                alert(result.message || intl.formatMessage({ id: 'DisputeDetails.error.fileLoad' }));
            } else {
                window.open(result.url, '_blank');
            }
        } catch (e) {
            alert(
                intl.formatMessage(
                    { id: 'DisputeDetails.error.generic' },
                    { message: e.message || intl.formatMessage({ id: 'DisputeDetails.error.fileOpen' }) }
                )
            );
        }
        setLoadingIndex(null);
    }

    function formatFileSize(bytes) {
        if (!bytes) return '';
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    }

    return (
        <div className={classes.AttachmentsSection}>
            <div className={classes.AttachmentsTitle}>
                <FormattedMessage id="DisputeDetails.attachments.title" values={{ count: attachments.length }} />
            </div>
            <div className={classes.AttachmentsList}>
                {attachments.map(function (att, idx) {
                    const isPhoto = att.type === 'photo';
                    const name =
                        att.file_name ||
                        (isPhoto
                            ? intl.formatMessage({ id: 'DisputeDetails.attachments.photo' })
                            : intl.formatMessage({ id: 'DisputeDetails.attachments.file' }));
                    const sizeStr = formatFileSize(att.file_size);

                    return (
                        <div key={idx} className={classes.AttachmentItem}>
                            <div className={classes.AttachmentIcon}>{isPhoto ? '\u{1F5BC}' : '\u{1F4CE}'}</div>
                            <div className={classes.AttachmentInfo}>
                                <span className={classes.AttachmentName}>{name}</span>
                                {sizeStr && <span className={classes.AttachmentSize}>{sizeStr}</span>}
                                {att.mime_type && <span className={classes.AttachmentMime}>{att.mime_type}</span>}
                            </div>
                            <button
                                className={classes.BtnAttachment}
                                onClick={function () {
                                    handleOpenAttachment(att, idx);
                                }}
                                disabled={loadingIndex === idx}
                            >
                                {loadingIndex === idx ? '...' : intl.formatMessage({ id: 'DisputeDetails.attachments.open' })}
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function DisputeDetails({ dispute, token, onUpdate, onClose: _onClose }) {
    const intl = useIntl();
    // Функция извлечения ID транзакции из текста сообщения
    const extractTransactionId = (messageText) => {
        if (!messageText) return '';
        // Ищем 7-значное число в тексте (например: 2415234, 2425157)
        const match = messageText.match(/\b\d{7}\b/);
        return match ? match[0] : '';
    };

    const [transactionInput, setTransactionInput] = useState(
        dispute.transactionId || extractTransactionId(dispute.messageText)
    );
    const [noteInput, setNoteInput] = useState(dispute.resolutionNote || '');
    const [providerStatus, setProviderStatus] = useState(null);
    const [isCheckingProvider, setIsCheckingProvider] = useState(false);
    const [checkResult, setCheckResult] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    // Сброс состояния при переключении между диспутами
    useEffect(() => {
        const extractedId = extractTransactionId(dispute.messageText);
        setTransactionInput(dispute.transactionId || extractedId);
        setNoteInput(dispute.resolutionNote || '');
        setProviderStatus(null);
        setCheckResult(null);
    }, [dispute.id]);

    // Автоматическая проверка транзакции при открытии диспута
    useEffect(() => {
        const extractedId = extractTransactionId(dispute.messageText);
        const txId = dispute.transactionId || extractedId;

        if (txId) {
            // Автоматически проверяем транзакцию
            setIsCheckingProvider(true);
            setCheckResult(null);

            const api = new Api({ token });
            api.checkDisputeTransaction(parseInt(txId, 10))
                .then((result) => {
                    setCheckResult(result);
                })
                .catch((e) => {
                    setCheckResult({ error: true, message: e.message || intl.formatMessage({ id: 'DisputeDetails.error.check' }) });
                })
                .finally(() => {
                    setIsCheckingProvider(false);
                });
        }
    }, [dispute.id]);

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        return (
            d.toLocaleDateString(intl.locale) +
            ' ' +
            d.toLocaleTimeString(intl.locale, { hour: '2-digit', minute: '2-digit' })
        );
    };

    // Check transaction status (without linking)
    async function handleCheckTransaction() {
        if (!transactionInput) return;

        setIsCheckingProvider(true);
        setCheckResult(null);
        try {
            const api = new Api({ token });
            const result = await api.checkDisputeTransaction(parseInt(transactionInput, 10));
            setCheckResult(result);
        } catch (e) {
            setCheckResult({ error: true, message: e.message || intl.formatMessage({ id: 'DisputeDetails.error.check' }) });
        }
        setIsCheckingProvider(false);
    }

    // Check provider status (for linked transaction)
    async function handleCheckProvider() {
        setIsCheckingProvider(true);
        setProviderStatus(null);
        try {
            const api = new Api({ token });
            const result = await api.checkDisputeProvider(dispute.id);
            setProviderStatus(result);
            if (!result.error) {
                onUpdate && onUpdate({ ...dispute, providerStatus: result.providerStatus });
            }
        } catch (e) {
            setProviderStatus({ error: true, message: e.message || intl.formatMessage({ id: 'DisputeDetails.error.check' }) });
        }
        setIsCheckingProvider(false);
    }

    // Update status
    async function handleStatusChange(newStatus) {
        setIsLoading(true);
        try {
            const api = new Api({ token });
            const payload = { status: newStatus };

            if (newStatus === 'resolved' && noteInput) {
                payload.note = noteInput;
            }

            const result = await api.updateDispute(dispute.id, payload);

            if (result.error) {
                alert(result.message || intl.formatMessage({ id: 'DisputeDetails.error.updateStatus' }));
            } else {
                onUpdate && onUpdate(result.dispute);
            }
        } catch (e) {
            alert(
                intl.formatMessage(
                    { id: 'DisputeDetails.error.generic' },
                    { message: e.message || intl.formatMessage({ id: 'DisputeDetails.error.unknown' }) }
                )
            );
        }
        setIsLoading(false);
    }

    // Save note
    async function handleSaveNote() {
        setIsLoading(true);
        try {
            const api = new Api({ token });
            const result = await api.updateDispute(dispute.id, { note: noteInput });

            if (result.error) {
                alert(result.message || intl.formatMessage({ id: 'DisputeDetails.error.saveNote' }));
            } else {
                onUpdate && onUpdate(result.dispute);
            }
        } catch (e) {
            alert(
                intl.formatMessage(
                    { id: 'DisputeDetails.error.generic' },
                    { message: e.message || intl.formatMessage({ id: 'DisputeDetails.error.unknown' }) }
                )
            );
        }
        setIsLoading(false);
    }

    return (
        <div className={classes.Card}>
            <div className={classes.Header}>
                <h5 className={classes.Title}>
                    <FormattedMessage id="DisputeDetails.title" /> #{dispute.id}
                </h5>
                <StatusPill status={dispute.status} />
            </div>

            <div className={classes.Body}>
                {/* Message Info */}
                <div className={classes.Section}>
                    <h6 className={classes.SectionTitle}>
                        <FormattedMessage id="DisputeDetails.section.message" />
                    </h6>
                    <div className={classes.InfoGrid}>
                        <div className={classes.InfoItem}>
                            <span className={classes.InfoLabel}><FormattedMessage id="DisputeDetails.label.chat" /></span>
                            <span className={classes.InfoValue}>{dispute.chatTitle || '-'}</span>
                        </div>
                        <div className={classes.InfoItem}>
                            <span className={classes.InfoLabel}><FormattedMessage id="DisputeDetails.label.sender" /></span>
                            <span className={classes.InfoValue}>{dispute.senderName || '-'}</span>
                        </div>
                        <div className={classes.InfoItem}>
                            <span className={classes.InfoLabel}><FormattedMessage id="DisputeDetails.label.date" /></span>
                            <span className={classes.InfoValue}>{formatDate(dispute.createdAt)}</span>
                        </div>
                        <div className={classes.InfoItem}>
                            <span className={classes.InfoLabel}><FormattedMessage id="DisputeDetails.label.attachment" /></span>
                            <span className={classes.InfoValue}>
                                {dispute.hasAttachment
                                    ? intl.formatMessage({ id: 'DisputeDetails.value.yes' })
                                    : intl.formatMessage({ id: 'DisputeDetails.value.no' })}
                            </span>
                        </div>
                    </div>

                    {dispute.messageText && <div className={classes.MessageBox}>{dispute.messageText}</div>}

                    {/* Attachments */}
                    {dispute.attachments && dispute.attachments.length > 0 && (
                        <AttachmentsList attachments={dispute.attachments} disputeId={dispute.id} token={token} />
                    )}
                </div>

                {/* Transaction & Provider Status */}
                <div className={classes.Section}>
                    <h6 className={classes.SectionTitle}>
                        <FormattedMessage id="DisputeDetails.section.transaction" />
                    </h6>

                    <div className={classes.TransactionRow}>
                        <input
                            type="text"
                            className={classes.Input}
                            placeholder={intl.formatMessage({ id: 'DisputeDetails.transaction.placeholder' })}
                            value={transactionInput}
                            onChange={(e) => setTransactionInput(e.target.value)}
                        />
                        <button
                            className={classes.BtnSecondary}
                            onClick={handleCheckTransaction}
                            disabled={isCheckingProvider || !transactionInput}
                        >
                            {isCheckingProvider ? <Loader isString /> : intl.formatMessage({ id: 'DisputeDetails.transaction.check' })}
                        </button>
                    </div>

                    {/* Check result for manual transaction input */}
                    {checkResult && (
                        <div className={classes.ProviderResult}>
                            {checkResult.error ? (
                                <div className={classes.ErrorBox}>{checkResult.message}</div>
                            ) : (
                                <React.Fragment>
                                    <div className={classes.StatusRow}>
                                        <span><FormattedMessage id="DisputeDetails.transaction.ourStatus" /></span>
                                        <strong>{checkResult.ourStatusLabel || checkResult.ourStatus}</strong>
                                    </div>
                                    {checkResult.merchantName && (
                                        <div className={classes.StatusRow}>
                                            <span><FormattedMessage id="DisputeDetails.transaction.merchant" /></span>
                                            <strong>{checkResult.merchantName}</strong>
                                        </div>
                                    )}
                                    {checkResult.amount && (
                                        <div className={classes.StatusRow}>
                                            <span><FormattedMessage id="DisputeDetails.transaction.amount" /></span>
                                            <strong>
                                                {checkResult.amount} {checkResult.currency}
                                            </strong>
                                        </div>
                                    )}
                                    {checkResult.providerStatus && (
                                        <div className={classes.ProviderBox}>
                                            <div className={classes.StatusRow}>
                                                <span><FormattedMessage id="DisputeDetails.transaction.provider" /></span>
                                                <strong>{checkResult.providerStatus.provider}</strong>
                                            </div>
                                            <div className={classes.StatusRow}>
                                                <span><FormattedMessage id="DisputeDetails.transaction.providerStatus" /></span>
                                                <strong>
                                                    {checkResult.providerStatus.statusHuman ||
                                                        checkResult.providerStatus.providerStatus}
                                                </strong>
                                            </div>
                                        </div>
                                    )}
                                </React.Fragment>
                            )}
                        </div>
                    )}

                    {/* Linked transaction info */}
                    {dispute.transactionId && (
                        <div className={classes.LinkedTransaction}>
                            <div className={classes.StatusRow}>
                                <span><FormattedMessage id="DisputeDetails.linked.transaction" /></span>
                                <strong>#{dispute.transactionId}</strong>
                            </div>
                            {dispute.merchantName && (
                                <div className={classes.StatusRow}>
                                    <span><FormattedMessage id="DisputeDetails.linked.merchant" /></span>
                                    <strong>{dispute.merchantName}</strong>
                                </div>
                            )}
                            {dispute.ourStatus && (
                                <div className={classes.StatusRow}>
                                    <span><FormattedMessage id="DisputeDetails.linked.statusAtCreation" /></span>
                                    <strong>{dispute.ourStatus}</strong>
                                </div>
                            )}
                            {dispute.ourStatusCurrent && (
                                <div className={classes.StatusRow}>
                                    <span><FormattedMessage id="DisputeDetails.linked.currentStatus" /></span>
                                    <strong>{dispute.ourStatusCurrent}</strong>
                                </div>
                            )}

                            <button
                                className={classes.BtnCheck}
                                onClick={handleCheckProvider}
                                disabled={isCheckingProvider}
                            >
                                {isCheckingProvider
                                    ? <Loader isString />
                                    : intl.formatMessage({ id: 'DisputeDetails.linked.checkProvider' })}
                            </button>

                            {/* Provider status result */}
                            {(providerStatus || dispute.providerStatus) && (
                                <div className={classes.ProviderBox}>
                                    <h6><FormattedMessage id="DisputeDetails.linked.providerResponse" /></h6>
                                    {(providerStatus || dispute.providerStatus).error ? (
                                        <div className={classes.ErrorBox}>
                                            {(providerStatus || dispute.providerStatus).message}
                                        </div>
                                    ) : (
                                        <pre className={classes.JsonBox}>
                                            {JSON.stringify(providerStatus || dispute.providerStatus, null, 2)}
                                        </pre>
                                    )}
                                    {dispute.providerCheckedAt && (
                                        <div className={classes.CheckedAt}>
                                            <FormattedMessage
                                                id="DisputeDetails.linked.lastCheck"
                                                values={{ date: formatDate(dispute.providerCheckedAt) }}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Resolution */}
                <div className={classes.Section}>
                    <h6 className={classes.SectionTitle}>
                        <FormattedMessage id="DisputeDetails.section.resolution" />
                    </h6>

                    <textarea
                        className={classes.Textarea}
                        placeholder={intl.formatMessage({ id: 'DisputeDetails.resolution.placeholder' })}
                        value={noteInput}
                        onChange={(e) => setNoteInput(e.target.value)}
                        rows={3}
                    />

                    <div className={classes.ActionButtons}>
                        {dispute.status === 'new' && (
                            <button
                                className={classes.BtnOrange}
                                onClick={() => handleStatusChange('in_progress')}
                                disabled={isLoading}
                            >
                                <FormattedMessage id="DisputeDetails.action.toWork" />
                            </button>
                        )}

                        {(dispute.status === 'new' || dispute.status === 'in_progress') && (
                            <button
                                className={classes.BtnGreen}
                                onClick={() => handleStatusChange('resolved')}
                                disabled={isLoading}
                            >
                                <FormattedMessage id="DisputeDetails.action.resolved" />
                            </button>
                        )}

                        {dispute.status === 'resolved' && (
                            <button
                                className={classes.BtnGray}
                                onClick={() => handleStatusChange('archived')}
                                disabled={isLoading}
                            >
                                <FormattedMessage id="DisputeDetails.action.archive" />
                            </button>
                        )}

                        <button className={classes.BtnSecondary} onClick={handleSaveNote} disabled={isLoading}>
                            <FormattedMessage id="DisputeDetails.action.saveNote" />
                        </button>
                    </div>

                    {dispute.resolvedByName && (
                        <div className={classes.ResolvedInfo}>
                            <FormattedMessage
                                id="DisputeDetails.resolution.resolvedBy"
                                values={{ name: dispute.resolvedByName, date: formatDate(dispute.resolvedAt) }}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

DisputeDetails.defaultProps = {
    onUpdate: () => {},
    onClose: () => {},
};

export default DisputeDetails;



// WEBPACK FOOTER //
// ./src/Components/ComponentsOfAdministrator/DisputeDetails/index.js